import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { apiRequest } from './client';
import type { AuthRepository, AuthResult, WebAuthnCredentialSummary } from '../../application/ports/AuthRepository';
import type { User } from '../../domain/User';

export class HttpAuthRepository implements AuthRepository {
  register(email: string, password: string, name: string) {
    return apiRequest<AuthResult>('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) });
  }

  login(email: string, password: string) {
    return apiRequest<AuthResult>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  }

  refreshAccessToken() {
    return apiRequest<AuthResult>('/auth/refresh', { method: 'POST' });
  }

  logout() {
    return apiRequest<void>('/auth/logout', { method: 'POST' });
  }

  fetchMe() {
    return apiRequest<{ user: User }>('/auth/me');
  }

  updateMe(name: string) {
    return apiRequest<{ user: User }>('/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) });
  }

  changePassword(oldPassword: string, newPassword: string) {
    return apiRequest<void>('/auth/change-password', { method: 'POST', body: JSON.stringify({ oldPassword, newPassword }) });
  }

  forgotPassword(email: string) {
    return apiRequest<{ message: string }>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  }

  resetPassword(token: string, newPassword: string) {
    return apiRequest<void>('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword }) });
  }

  getWebAuthnRegisterOptions() {
    return apiRequest<PublicKeyCredentialCreationOptionsJSON>('/auth/webauthn/register-options', { method: 'POST' });
  }

  verifyWebAuthnRegistration(response: RegistrationResponseJSON, deviceName: string) {
    return apiRequest<void>('/auth/webauthn/register-verify', {
      method: 'POST',
      body: JSON.stringify({ response, deviceName }),
    });
  }

  getWebAuthnLoginOptions() {
    return apiRequest<PublicKeyCredentialRequestOptionsJSON>('/auth/webauthn/login-options', { method: 'POST' });
  }

  verifyWebAuthnLogin(response: AuthenticationResponseJSON) {
    return apiRequest<AuthResult>('/auth/webauthn/login-verify', { method: 'POST', body: JSON.stringify({ response }) });
  }

  listWebAuthnCredentials() {
    return apiRequest<{ credentials: WebAuthnCredentialSummary[] }>('/auth/webauthn/credentials').then((r) => r.credentials);
  }

  deleteWebAuthnCredential(id: string) {
    return apiRequest<void>(`/auth/webauthn/credentials/${id}`, { method: 'DELETE' });
  }
}
